import { useEffect, useState } from "react";
import { ISurvey } from "src/api/types";
import { ISurveyEvalResponse } from "src/api/types";
import {
  IChartPoint,
  IFormattedSurveyEvaluations,
  IUseSurveyEvaluationsHistoryParams,
} from "./types";
import { createChartPoint } from "./utils/createChartPoint/createChartPoint";
import { getDateKey } from "./utils/getDateKey/getDateKey";
import { sortByDate } from "./utils/sortByDate/sortByDate";

type Id = string | number;

export const useSurveyEvaluationsHistory = ({
  currentCoachId,
  loadSurveys,
  schoolSlug,
  surveys,
}: IUseSurveyEvaluationsHistoryParams) => {
  const [hasError, toggleHasError] = useState(false);
  const [isLoading, toggleIsLoading] = useState(false);
  const [finishedCurrentCoachSurveys, setFinishedCurrentCoachSurveys] =
    useState<ISurvey[]>([]);
  const [surveyEvaluations, setSurveyEvaluations] = useState<
    ISurveyEvalResponse[]
  >([]);
  const [formattedSurveyEvaluations, setFormattedSurveyEvaluations] =
    useState<IFormattedSurveyEvaluations | null>(null);

  const params = {
    isParentResponses: "false",
    schoolSlug,
  };
  const queryParams = new URLSearchParams(params).toString();
  const createApiCall = (endpoint: string) => `/api${endpoint}`;
  const getSurveyEvaluationsEndpoint = (surveyId: Id, coachId: Id) =>
    `/survey/${surveyId}/evaluation/${coachId}?${queryParams}`;

  useEffect(() => {
    loadSurveys();
  }, []);

  useEffect(() => {
    toggleHasError(false);
    setFinishedCurrentCoachSurveys(
      surveys.filter((survey) => {
        const isFinished = survey.responseCollectionEndDate !== null;
        const isCurrentCoachSurvey = survey.coachIds.includes(currentCoachId);
        return isFinished && isCurrentCoachSurvey;
      })
    );
  }, [surveys, currentCoachId]);

  useEffect(() => {
    if (finishedCurrentCoachSurveys.length === 0) {
      setSurveyEvaluations([]);
    } else {
      const surveyEvaluationPromises = finishedCurrentCoachSurveys.map(
        (survey) => {
          return async (): Promise<ISurveyEvalResponse> => {
            const response = await fetch(
              createApiCall(
                getSurveyEvaluationsEndpoint(survey.id, currentCoachId)
              )
            );
            return await response.json();
          };
        }
      );

      const fetchData = async () => {
        try {
          toggleIsLoading(true);
          const results = await Promise.all(
            surveyEvaluationPromises.map((promise) => promise())
          );
          setSurveyEvaluations(results);
          toggleIsLoading(false);
        } catch (err) {
          console.error(err);
          toggleHasError(true);
          toggleIsLoading(false);
        }
      };
      fetchData();
    }
  }, [finishedCurrentCoachSurveys]);

  useEffect(() => {
    const expectations: IChartPoint[] = [];
    const warmth: IChartPoint[] = [];
    const overall: IChartPoint[] = [];

    if (surveyEvaluations.length === 0) {
      setFormattedSurveyEvaluations(null);
    } else {
      const dateHash = {};

      surveyEvaluations.sort(sortByDate).forEach((surveyEvaluation, index) => {
        const { warmth: surveyWarmth, expectations: surveyExpectations } =
          surveyEvaluation.scaleResponseSummaries;

        const date = getDateKey(surveyEvaluation.startDate, dateHash);
        const sportId = surveyEvaluation.sportId;

        const expectationsCoachAverage = surveyExpectations.coachTotalAverage;
        const expectationsStudentAverage =
          surveyExpectations.studentTotalAverage;

        const warmthCoachAverage = surveyWarmth.coachTotalAverage;
        const warmthStudentAverage = surveyWarmth.studentTotalAverage;

        const overallCoachAverage =
          (expectationsCoachAverage + warmthCoachAverage) / 2;
        const overallStudentAverage =
          (expectationsStudentAverage + warmthStudentAverage) / 2;

        expectations[index] = createChartPoint(
          expectationsCoachAverage,
          expectationsStudentAverage,
          date,
          sportId
        );
        warmth[index] = createChartPoint(
          warmthCoachAverage,
          warmthStudentAverage,
          date,
          sportId
        );
        overall[index] = createChartPoint(
          overallCoachAverage,
          overallStudentAverage,
          date,
          sportId
        );
      });

      setFormattedSurveyEvaluations({
        expectations,
        overall,
        warmth,
      });
    }
  }, [surveyEvaluations]);

  return {
    formattedSurveyEvaluations,
    hasError,
    isLoading,
  };
};



// WEBPACK FOOTER //
// ./src/features/director/pages/SurveyDataCenter/hooks/useSurveyEvaluationsHistory.tsx